import React, { useEffect, useState } from "react";
import { links } from "../../config/links";

interface HeroProps {
  onOpenModal: () => void;
}

interface Oferta {
  embalagem: string;
  modalidade: string;
  preco: number;
  minimo: string;
}

const ofertas: Oferta[] = [
  { embalagem: "Caixa 12 x 1 L", modalidade: "CIF", preco: 348.0, minimo: "2 cx" },
  { embalagem: "Balde 20 L", modalidade: "CIF", preco: 528.0, minimo: "1 bd" },
  { embalagem: "Tambor 200 L", modalidade: "FOB", preco: 4780.0, minimo: "1 tb" },
];

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Hero: React.FC<HeroProps> = ({ onOpenModal }) => {
  const [selecionada, setSelecionada] = useState(1);
  const [pulso, setPulso] = useState(false);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const t = window.setInterval(() => {
      setPulso(true);
      window.setTimeout(() => setPulso(false), 900);
    }, 4200);
    return () => window.clearInterval(t);
  }, []);

  const atual = ofertas[selecionada];

  return (
    <section id="top" className="relative overflow-hidden border-b border-bone-300 bg-bone">
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-site items-center gap-12 px-5 py-16 lg:grid-cols-[1fr_1fr] lg:gap-16 lg:px-8 lg:py-24">
        <div>
          <p className="eyebrow">Marketplace do agronegócio</p>
          <h1 className="mt-5 font-display text-display-lg font-extrabold text-soil">
            Compre sem sair
            <br />
            da fazenda.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-soil/75">
            O catálogo dos parceiros com quem você já negocia, de semente a peça,
            com embalagem, frete e prazo na tela. Você monta o pedido, paga no
            app e acompanha até a porteira. Sem ligar e sem cotar.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={onOpenModal} className="cta-solid">
              Quero o app
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 7.5h9M8.5 4l3.5 3.5L8.5 11" />
              </svg>
            </button>
            <a href={links.partnerSignup} className="cta-ghost">
              Cadastrar minha empresa
            </a>
          </div>

          <p className="mt-7 font-mono text-xs uppercase tracking-[0.14em] text-soil/45">
            Sementes · Fertilizantes · Combustíveis · Peças e Implementos ·
            Agropecuárias
          </p>
        </div>

        <div className="panel shadow-[0_1px_0_rgba(20,32,14,0.04),0_18px_50px_-24px_rgba(20,32,14,0.28)]">
          <div className="panel-head">
            <div className="flex items-center gap-2.5">
              <span
                className={`h-1.5 w-1.5 rounded-full bg-agro-accent transition-opacity duration-500 ${
                  pulso ? "opacity-100" : "opacity-40"
                }`}
              />
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-soil/60">
                Catálogo do seu parceiro
              </span>
            </div>
            <span className="rounded border border-bone-300 bg-bone px-1.5 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-soil/45">
              exemplo
            </span>
          </div>

          <div className="border-b border-bone-300 px-4 py-4 sm:px-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-display text-xl font-bold text-soil">
                  Óleo de motor 15W40 CI-4
                </p>
                <p className="mt-1 text-sm text-soil/60">
                  Vale Verde · Rondonópolis, MT
                </p>
              </div>
              <span className="shrink-0 rounded bg-agro-lighter px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.1em] text-agro-primary">
                combustíveis
              </span>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 border-b border-bone-300 bg-bone/60 px-4 py-2 sm:gap-x-6 sm:px-5">
            <span className="field-label">Embalagem</span>
            <span className="field-label text-right">Preço</span>
            <span className="field-label text-right">Mínimo</span>
          </div>

          <div role="radiogroup" aria-label="Embalagem" className="divide-y divide-bone-200">
            {ofertas.map((o, i) => {
              const ativa = i === selecionada;
              return (
                <button
                  key={`${o.embalagem}-${o.modalidade}`}
                  role="radio"
                  aria-checked={ativa}
                  onClick={() => setSelecionada(i)}
                  className={`grid w-full grid-cols-[1fr_auto_auto] items-center gap-x-3 px-4 py-3 text-left transition-colors sm:gap-x-6 sm:px-5 ${
                    ativa ? "bg-soy/10" : "hover:bg-bone/70"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${
                        ativa ? "border-soy-dark" : "border-soil/25"
                      }`}
                    >
                      {ativa && <span className="h-1.5 w-1.5 rounded-full bg-soy-dark" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-soil">
                        {o.embalagem}
                      </span>
                      <span className="block font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-soil/45">
                        frete {o.modalidade}
                      </span>
                    </span>
                  </div>
                  <span
                    className={`num text-right text-sm ${
                      ativa ? "font-bold text-soy-dark" : "font-medium text-soil/80"
                    }`}
                  >
                    {brl(o.preco)}
                  </span>
                  <span className="num text-right text-xs text-soil/50">{o.minimo}</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1.5 border-t border-bone-300 px-4 py-3 sm:px-5">
            <span className="text-xs text-soil/60">
              Entrega até <span className="num font-semibold text-soil">12/09</span>
            </span>
            <span className="text-xs text-soil/60">
              Pagamento em <span className="num font-semibold text-soil">30 dias</span>
            </span>
            <span className="text-xs text-soil/60">
              Até <span className="num font-semibold text-soil">12x</span> no cartão
            </span>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-bone-300 bg-soil px-4 py-4 sm:px-5">
            <div>
              <p className="font-mono text-[0.625rem] uppercase tracking-[0.16em] text-soy/80">
                {atual.embalagem} · {atual.modalidade}
              </p>
              <p className="num mt-1 text-xl font-bold text-white">
                R$ {brl(atual.preco)}
              </p>
            </div>
            <span className="cta-gold pointer-events-none px-5 py-2.5 text-sm">
              Adicionar ao carrinho
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
